// The channel list's "NEW MENTIONS" / "NEW UNREADS" pills, like Discord's: when a conversation with unread messages
// is scrolled out of the sidebar's view, a pill at the bottom of the list (it's below) or at the top under the head
// (it's above) says so; clicking it scrolls the nearest one in that direction into view, so repeated clicks walk
// through them. A mention anywhere out of view in that direction makes the pill red ("NEW MENTIONS") and clicking goes
// to the nearest mention; otherwise it's "NEW UNREADS".
//
// Rows opt in with `data-unread="mention" | "unread"` (Server.tsx's channel links, DmList's rows). Collapsed categories
// already keep their unread channels in the list (like Discord), so they count too. Muted channels don't mark plain
// unreads (Discord doesn't count them either), only mentions.
//
// Speed: nothing here runs per scroll frame in React. Scrolls and resizes schedule one measurement per animation frame
// (a handful of getBoundingClientRect calls over the marked rows only), and state is set only when what the pills
// show actually changes, so only this small component re-renders, and rarely.
import { useEffect, useLayoutEffect, useRef, useState, type RefObject } from "react";

type Kind = "mention" | "unread";
type Pills = { above: Kind | null; below: Kind | null; top: number; bottom: number };
const NONE: Pills = { above: null, below: null, top: 0, bottom: 0 };
/** Room left between a row scrolled to by a pill and the edge (so it isn't under the pill). */
const LANDING = 52;

export function UnreadPills(props: {
  scrollerRef: RefObject<HTMLDivElement | null>;
  headRef: RefObject<HTMLDivElement | null>;
  /** Changes whenever which rows are marked (or the list's shape) may have changed: unread set, collapsed, channels. */
  signature: string;
}) {
  const { scrollerRef, headRef, signature } = props;
  const [pills, setPills] = useState<Pills>(NONE);
  const last = useRef<Pills>(NONE);
  const frame = useRef(0);

  /** The scroller's visible band in viewport coordinates (the head covers its top when it sits on a banner). */
  const band = () => {
    const scroller = scrollerRef.current!;
    const r = scroller.getBoundingClientRect();
    const head = headRef.current;
    let top = r.top;
    if (head) {
      const h = head.getBoundingClientRect();
      if (h.bottom > top && h.top <= top) top = h.bottom;
    }
    return { top, bottom: r.bottom, rect: r };
  };

  /** Marked rows out of view above and below, nearest first. */
  const outOfView = () => {
    const scroller = scrollerRef.current;
    if (!scroller) return null;
    const b = band();
    const above: { el: HTMLElement; kind: Kind }[] = [];
    const below: { el: HTMLElement; kind: Kind }[] = [];
    for (const el of scroller.querySelectorAll<HTMLElement>("[data-unread]")) {
      const r = el.getBoundingClientRect();
      if (!r.height) continue;
      const mid = (r.top + r.bottom) / 2;
      const kind = el.dataset.unread === "mention" ? "mention" : "unread";
      if (mid < b.top) above.push({ el, kind });
      else if (mid > b.bottom) below.push({ el, kind });
    }
    above.reverse(); // nearest (lowest on the page) first
    return { b, above, below };
  };

  const measure = () => {
    frame.current = 0;
    const scroller = scrollerRef.current;
    const found = outOfView();
    if (!scroller || !found) return;
    const side = scroller.offsetParent as HTMLElement | null;
    const sideRect = side?.getBoundingClientRect() ?? found.b.rect;
    const kindOf = (list: { kind: Kind }[]): Kind | null => (list.some((x) => x.kind === "mention") ? "mention" : list.length ? "unread" : null);
    const next: Pills = {
      above: kindOf(found.above),
      below: kindOf(found.below),
      top: Math.round(found.b.top - sideRect.top),
      bottom: Math.round(sideRect.bottom - found.b.bottom),
    };
    const p = last.current;
    if (p.above === next.above && p.below === next.below && p.top === next.top && p.bottom === next.bottom) return;
    last.current = next;
    setPills(next);
  };
  const schedule = () => {
    if (!frame.current) frame.current = requestAnimationFrame(measure);
  };

  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    scroller.addEventListener("scroll", schedule, { passive: true });
    const ro = typeof ResizeObserver === "function" ? new ResizeObserver(schedule) : null;
    ro?.observe(scroller);
    if (scroller.lastElementChild) ro?.observe(scroller.lastElementChild);
    window.addEventListener("resize", schedule);
    return () => {
      scroller.removeEventListener("scroll", schedule);
      ro?.disconnect();
      window.removeEventListener("resize", schedule);
      if (frame.current) cancelAnimationFrame(frame.current);
      frame.current = 0;
    };
  }, []);
  // Rows changed (a message arrived, something was read, a category opened): measure after this render lands.
  useLayoutEffect(schedule, [signature]);

  const go = (dir: "above" | "below") => {
    const scroller = scrollerRef.current;
    const found = outOfView();
    if (!scroller || !found) return;
    const list = found[dir];
    const target = list.find((x) => x.kind === "mention") ?? list[0];
    if (!target) return;
    const r = target.el.getBoundingClientRect();
    const delta = dir === "below" ? r.bottom - (found.b.bottom - LANDING) : r.top - (found.b.top + LANDING);
    const smooth = !window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    scroller.scrollBy({ top: delta, behavior: smooth ? "smooth" : "auto" });
  };

  const pill = (dir: "above" | "below", kind: Kind) => (
    <button
      type="button"
      className={`unread-jump ${dir} ${kind}`}
      style={dir === "above" ? { top: pills.top + 8 } : { bottom: pills.bottom + 8 }}
      onClick={() => go(dir)}
      aria-label={`${kind === "mention" ? "New mentions" : "New unread messages"} ${dir === "above" ? "above" : "below"}: scroll to it`}
    >
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={dir === "above" ? { transform: "rotate(180deg)" } : undefined}>
        <path d="M12 5v14M5 12l7 7 7-7" />
      </svg>
      {kind === "mention" ? "New mentions" : "New unreads"}
    </button>
  );
  return (
    <>
      {pills.above && pill("above", pills.above)}
      {pills.below && pill("below", pills.below)}
    </>
  );
}
